import React, { memo } from 'react';
import { Button, Card, CardBody, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useUpdatePatchMutation } from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import {
  BusinessSettingsUpdateDto,
  TenantSettingsFormValues,
} from 'components/Types/Tenant/business-settings.type';

interface EditBusinessSettingsFormProps {
  tenantId: string;
  settingsData?: TenantSettingsFormValues | null;
}

const validationSchema = Yup.object({
  name: Yup.string().trim().required('Business name is required'),
  contactPhone: Yup.string().trim().required('Contact phone is required'),
  websiteUrl: Yup.string().trim().url('Invalid website URL').nullable(),
  contactEmail: Yup.string().trim().email('Invalid email').nullable(),
  zipCode: Yup.string().trim().nullable(),
  streetAddress: Yup.string().trim().nullable(),
  metaDescription: Yup.string().trim().nullable(),
  metaKeywords: Yup.string().trim().nullable(),
  industry: Yup.string().trim().nullable(),
});

const EditBusinessSettingsForm: React.FC<EditBusinessSettingsFormProps> = ({
  tenantId,
  settingsData,
}) => {
  const { mutate: updateSettings, isPending: isUpdating } = useUpdatePatchMutation<
    TenantSettingsFormValues,
    BusinessSettingsUpdateDto
  >(API_ENDPOINTS.TENANT.BUSINESS.UPDATE_SETTINGS(tenantId));

  const formik = useFormik<BusinessSettingsUpdateDto>({
    initialValues: {
      name: settingsData?.name ?? '',
      description: settingsData?.description ?? '',
      websiteUrl: settingsData?.websiteUrl ?? '',
      contactPhone: settingsData?.contactPhone ?? '',
      contactEmail: settingsData?.contactEmail ?? '',
      country: settingsData?.country ?? '',
      state: settingsData?.state ?? '',
      city: settingsData?.city ?? '',
      zipCode: settingsData?.zipCode ?? '',
      streetAddress: settingsData?.streetAddress ?? '',
      metaDescription: settingsData?.metaDescription ?? '',
      metaKeywords: settingsData?.metaKeywords ?? '',
      industry: settingsData?.industry ?? '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      if (!tenantId) return;
      updateSettings({ data: values, params: {} });
    },
  });

  return (
    <Card className="shadow-sm">
      <CardBody>
        <h4 className="mb-4">Business Information</h4>
        <form onSubmit={formik.handleSubmit} noValidate>
          <Row>
            <Col lg={6} md={6} className="mb-3">
              <FormGroup>
                <Label for="name">Business Name</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.name && formik.errors.name)}
                />
                {formik.touched.name && formik.errors.name && (
                  <div className="text-danger mt-1">{formik.errors.name}</div>
                )}
              </FormGroup>
            </Col>
            <Col lg={6} md={6} className="mb-3">
              <FormGroup>
                <Label for="contactPhone">Contact Phone</Label>
                <Input
                  id="contactPhone"
                  name="contactPhone"
                  type="text"
                  value={formik.values.contactPhone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.contactPhone && formik.errors.contactPhone)}
                />
                {formik.touched.contactPhone && formik.errors.contactPhone && (
                  <div className="text-danger mt-1">{formik.errors.contactPhone}</div>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col lg={6} md={6} className="mb-3">
              <FormGroup>
                <Label for="contactEmail">Contact Email</Label>
                <Input
                  id="contactEmail"
                  name="contactEmail"
                  type="email"
                  value={formik.values.contactEmail}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.contactEmail && formik.errors.contactEmail)}
                />
                {formik.touched.contactEmail && formik.errors.contactEmail && (
                  <div className="text-danger mt-1">{formik.errors.contactEmail}</div>
                )}
              </FormGroup>
            </Col>
            <Col lg={6} md={6} className="mb-3">
              <FormGroup>
                <Label for="websiteUrl">Website URL</Label>
                <Input
                  id="websiteUrl"
                  name="websiteUrl"
                  type="url"
                  value={formik.values.websiteUrl}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.websiteUrl && formik.errors.websiteUrl)}
                />
                {formik.touched.websiteUrl && formik.errors.websiteUrl && (
                  <div className="text-danger mt-1">{formik.errors.websiteUrl}</div>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col lg={6} md={6} className="mb-3">
              <FormGroup>
                <Label for="country">Country</Label>
                <Input
                  id="country"
                  name="country"
                  type="text"
                  value={formik.values.country}
                  onChange={formik.handleChange}
                />
              </FormGroup>
            </Col>
            <Col lg={6} md={6} className="mb-3">
              <FormGroup>
                <Label for="state">State</Label>
                <Input
                  id="state"
                  name="state"
                  type="text"
                  value={formik.values.state}
                  onChange={formik.handleChange}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col lg={6} md={6} className="mb-3">
              <FormGroup>
                <Label for="city">City</Label>
                <Input
                  id="city"
                  name="city"
                  type="text"
                  value={formik.values.city}
                  onChange={formik.handleChange}
                />
              </FormGroup>
            </Col>
            <Col lg={6} md={6} className="mb-3">
              <FormGroup>
                <Label for="zipCode">Zip Code</Label>
                <Input
                  id="zipCode"
                  name="zipCode"
                  type="text"
                  value={formik.values.zipCode}
                  onChange={formik.handleChange}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col lg={12} md={12} className="mb-3">
              <FormGroup>
                <Label for="streetAddress">Street Address</Label>
                <Input
                  id="streetAddress"
                  name="streetAddress"
                  type="text"
                  value={formik.values.streetAddress}
                  onChange={formik.handleChange}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col lg={12} md={12} className="mb-3">
              <FormGroup>
                <Label for="description">Description</Label>
                <Input
                  id="description"
                  name="description"
                  type="textarea"
                  rows={4}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col lg={12} md={12} className="mb-3">
              <FormGroup>
                <Label for="industry">Industry</Label>
                <Input
                  id="industry"
                  name="industry"
                  type="text"
                  value={formik.values.industry}
                  onChange={formik.handleChange}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col lg={12} md={12} className="mb-3">
              <FormGroup>
                <Label for="metaDescription">Meta Description</Label>
                <Input
                  id="metaDescription"
                  name="metaDescription"
                  type="textarea"
                  rows={3}
                  value={formik.values.metaDescription}
                  onChange={formik.handleChange}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col lg={12} md={12} className="mb-3">
              <FormGroup>
                <Label for="metaKeywords">Meta Keywords</Label>
                <Input
                  id="metaKeywords"
                  name="metaKeywords"
                  type="text"
                  value={formik.values.metaKeywords}
                  onChange={formik.handleChange}
                />
              </FormGroup>
            </Col>
          </Row>

          <div className="d-flex justify-content-end mt-4">
            <Button color="primary" type="submit" disabled={isUpdating}>
              {isUpdating ? 'Saving...' : 'Save Business Settings'}
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
};

export default memo(EditBusinessSettingsForm);
